import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  // Surface a clear error in dev/staging — production deploys must have these set.
  console.error(
    'Supabase env vars missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local',
  );
}

export const supabase: SupabaseClient<Database> = createClient<Database>(
  supabaseUrl ?? 'https://missing-url.supabase.co',
  supabaseAnonKey ?? 'missing-anon-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
    global: {
      headers: { 'x-client-info': 'whats-on/1.0' },
    },
  },
);

export enum OperationType {
  SELECT = 'select',
  INSERT = 'insert',
  UPDATE = 'update',
  DELETE = 'delete',
  RPC = 'rpc',
}

export interface SupabaseErrorInfo {
  error: string;
  operation: OperationType;
  table: string | null;
  userId: string | null | undefined;
}

export function handleSupabaseError(
  error: unknown,
  operation: OperationType,
  table: string | null,
) {
  const info: SupabaseErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    operation,
    table,
    userId: undefined,
  };
  // Best-effort enrich with current user
  void supabase.auth.getUser().then(({ data }) => {
    info.userId = data.user?.id;
    console.error('Supabase error:', JSON.stringify(info));
  });
  throw new Error(JSON.stringify(info));
}

/**
 * Build a public click-tracking URL for an event.
 * Browser hits this; Edge Function logs the click and 302s to link_url.
 */
export function trackingUrlFor(slug: string | null | undefined, params?: Record<string, string>) {
  if (!slug) return null;
  const base = (supabaseUrl ?? '').replace(/\/$/, '');
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return `${base}/functions/v1/r/${slug}${qs}`;
}

/** Build the public OG image URL for an event. */
export function ogImageUrlFor(eventId: string) {
  const base = (supabaseUrl ?? '').replace(/\/$/, '');
  return `${base}/functions/v1/og-event/${eventId}`;
}
